import BodyText from 'LuxKit/Typography/BodyText'
import Colour from 'components/utils/Colour'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import { SVG_ICON_SIZES, type SvgIconComponent, type SvgIconProps, type SvgIconSize } from 'LuxKit/SvgIcon'
import { rem } from 'polished'
import type { ComponentProps, ReactNode } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'
import TourCampaignFlightCreditDescription from 'components/Tours/TourCampaignFlightCreditDescription'
import { TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID } from 'constants/tours'

const ROW_ICON_SIZE: Record<'small' | 'medium', SvgIconSize> = {
  small: 'XS',
  medium: 'S',
}

const Container = styled.div<{ $iconSize: SvgIconSize }>`
  display: grid;
  grid-template: "icon description" auto / ${props => SVG_ICON_SIZES[props.$iconSize]} minmax(0px, 1fr);
  gap: ${rem(4)};
  align-items: center;
`

const IconCell = styled.div`
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DescriptionCell = styled.div`
  grid-area: description;
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
`

type InheritedBodyTextProps = Pick<ComponentProps<typeof BodyText>, 'lineClamp'>

export type SearchTileRowInclusion = App.OfferInclusion & {
  propertyIconName?: App.Property.IconName
  colour?: ComponentProps<typeof BodyText>['colour']
  iconColour?: SvgIconProps['colour']
  Icon?: SvgIconComponent
}

interface Props extends InheritedBodyTextProps {
  inclusion: SearchTileRowInclusion
  /**
   * @default `inclusion.isHighlighted``
   */
  isHighlighted?: boolean
  endIcon?: ReactNode
  /**
   * @default medium
   */
  variant?: 'small' | 'medium'
  /**
   * When set, colours both the icon and the description.
   * Does not change font weight.
   */
  colour?: ComponentProps<typeof BodyText>['colour']
}

function SearchTileRowInclusionItem({
  inclusion,
  lineClamp,
  isHighlighted,
  endIcon,
  variant = 'medium',
  colour: colourOverride,
}: Props) {
  const Icon = useMemo(() => {
    if (inclusion.Icon) return inclusion.Icon
    if (inclusion.propertyIconName) return getPropertyIcon(inclusion.propertyIconName)

    return getOfferFeatureIcon(inclusion.symbol)
  }, [inclusion.Icon, inclusion.propertyIconName, inclusion.symbol])
  const highlighted = isHighlighted === undefined ? inclusion.isHighlighted : isHighlighted
  const isLuxPlusBonus = inclusion.isLuxPlusBonus
  const colour = colourOverride ?? inclusion.colour

  const iconColour = useMemo(() => {
    if (colour) return colour
    if (inclusion.iconColour) return inclusion.iconColour
    if (inclusion.id === TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID) return 'urgency'
    if (inclusion.isFlash) return 'exclusive'
    if (highlighted) return 'highlight-primary-dark'
    if (isLuxPlusBonus) return 'lux-plus'

    return undefined
  }, [colour, highlighted, inclusion.iconColour, inclusion.id, inclusion.isFlash, isLuxPlusBonus])

  const iconSize = ROW_ICON_SIZE[variant]

  return <Container $iconSize={iconSize}>
    <IconCell>
      <Icon
        size={iconSize}
        colour={iconColour}
        data-testid={inclusion.Icon ? inclusion.id : undefined}
      />
    </IconCell>
    <DescriptionCell>
      <BodyText
        variant={variant}
        weight={highlighted ? 'bold' : 'normal'}
        lineClamp={lineClamp}
      >
        {inclusion.id === TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID ?
            <TourCampaignFlightCreditDescription /> :
          colour ? <Colour value={colour}>{inclusion.description}</Colour> : inclusion.description}
      </BodyText>
      {endIcon}
    </DescriptionCell>
  </Container>
}

export default SearchTileRowInclusionItem
